import { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, X } from 'lucide-react';

const Hero = () => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [showEmergencyBox, setShowEmergencyBox] = useState(true);
  const fullText = "Expert Support for Your Website";
  
  // Add emergency box styles to ensure top positioning
  useEffect(() => {
    // Add a style tag to ensure emergency box is on top
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      .emergency-support-box {
        position: fixed !important;
        z-index: 9999999 !important;
      }
    `;
    document.head.appendChild(styleTag);
    
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);
  
  useEffect(() => {
    if (displayedText.length < fullText.length) {
      const typingTimer = setTimeout(() => {
        setDisplayedText(fullText.substring(0, displayedText.length + 1));
      }, 100);
      
      return () => clearTimeout(typingTimer);
    } else {
      setIsTypingComplete(true);
    }
  }, [displayedText]);

  return (
    <section className="support-hero relative flex items-center bg-gradient-to-br from-indigo-900 via-blue-800 to-indigo-900 min-h-screen py-12 md:py-16 overflow-hidden">
      {/* Abstract shapes background overlay */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-64 h-64 rounded-full bg-blue-600/20 blur-3xl -top-10 -right-10"></div>
        <div className="absolute w-96 h-96 rounded-full bg-indigo-600/20 blur-3xl -bottom-10 -left-10"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10 w-full">
        <div className="max-w-3xl mx-auto text-center">
          <span className="bg-white/10 text-white px-5 py-1.5 rounded-full text-sm font-semibold inline-block mb-6 md:mb-8 backdrop-blur-sm animate-fadeIn shadow-lg">
            GRAVITY POINT MEDIA
          </span>
          
          <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            <span className="typing-text">{displayedText}</span>
            <span className={`typing-cursor ${isTypingComplete ? 'blinking' : ''}`}>|</span>
          </h1>
          
          <h2 className="text-lg md:text-2xl text-white/90 font-medium mb-6 animate-fadeIn opacity-0" style={{
            animationDelay: '2.5s',
            animationFillMode: 'forwards'
          }}>
            Comprehensive Solutions to Help You Thrive in the Digital World
          </h2>
          
          <p className="text-base md:text-lg text-white/80 mb-6 md:mb-10 max-w-2xl mx-auto animate-fadeIn opacity-0" style={{
            animationDelay: '3s',
            animationFillMode: 'forwards'
          }}>
            Our expert team is ready to solve your website challenges and optimize your digital presence for success. We focus on solving your website pain points quickly and effectively.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 text-left text-white/90 bg-white/5 backdrop-blur-sm p-4 md:p-6 rounded-xl shadow-lg mb-6 md:mb-10 animate-fadeIn opacity-0" style={{
            animationDelay: '3.5s',
            animationFillMode: 'forwards'
          }}>
            {[
              'Predictable monthly pricing with no surprises',
              'Bug fixes, content updates, and image replacements',
              'Fast response times, with emergency support available',
              'Installing analytics, Meta pixels, and tracking tools'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3" style={{animationDelay: `${3.5 + index * 0.2}s`}}>
                <CheckCircle className="h-5 w-5 text-blue-300 flex-shrink-0 mt-0.5" />
                <span className="text-sm md:text-base">{item}</span>
              </div>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-5 mb-6 md:mb-10 animate-fadeIn opacity-0" style={{
            animationDelay: '4.5s',
            animationFillMode: 'forwards'
          }}>
            <a
              href="/register"
              className="rounded-md bg-primary px-6 py-3 md:px-8 md:py-4 text-center font-semibold text-white shadow-lg hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition-all hover:scale-105 hover:shadow-xl"
            >
              Get Started
            </a>
            <a
              href="#services"
              className="rounded-md bg-white/10 backdrop-blur-sm px-6 py-3 md:px-8 md:py-4 text-center font-semibold text-white hover:bg-white/20 border border-white/20 shadow-lg transition-all hover:scale-105"
            >
              Learn More
            </a>
          </div>
          
          <div className="hero-testimonial bg-white/10 backdrop-blur-sm p-4 md:p-6 rounded-xl max-w-2xl mx-auto shadow-lg animate-fadeIn opacity-0" style={{
            animationDelay: '5s',
            animationFillMode: 'forwards'
          }}>
            <p className="italic text-white/90 mb-2 md:mb-3 text-sm md:text-base">
              "Gravity Point Media fixed our WordPress WooCommerce checkout issue in under 3 hours when our previous developer couldn't figure it out for weeks."
            </p>
            <p className="text-white font-medium text-sm md:text-base">— Chime O., Diet in a Box</p>
          </div>
        </div>
      </div>
      
      <div className="support-pattern absolute inset-0 z-0 opacity-15"></div>
      
      {/* Emergency support box positioned for all screen sizes with maximum z-index */}
      {showEmergencyBox && (
        <>
          {/* Mobile backdrop overlay to ensure box stands out */}
          <div className="md:hidden fixed inset-0 z-[9998] animate-fadeIn opacity-0" style={{
            animationDelay: '5.5s',
            animationFillMode: 'forwards'
          }} onClick={() => setShowEmergencyBox(false)}></div>
          
          <div className="emergency-support-box fixed md:absolute bottom-4 right-4 md:bottom-6 md:right-6 lg:right-12 w-full max-w-[calc(100%-2rem)] md:max-w-sm z-[9999] animate-fadeIn opacity-0" style={{
            animationDelay: '5.5s',
            animationFillMode: 'forwards'
          }}>
            <div className="bg-white rounded-xl shadow-xl p-3 md:p-6 border-l-4 border-amber-500 hover:shadow-2xl transition-all duration-300 relative">
              {/* Close button */}
              <button 
                onClick={() => setShowEmergencyBox(false)}
                className="absolute top-1 right-1 md:top-2 md:right-2 rounded-full bg-gray-100 p-1 hover:bg-gray-200 transition-colors z-[9999]"
                aria-label="Close emergency support box"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
              
              <div className="flex items-center space-x-2 md:space-x-3 mb-2 md:mb-3">
                <div className="bg-amber-100 p-1.5 md:p-2 rounded-lg">
                  <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-amber-500" />
                </div>
                <h3 className="text-gray-800 font-bold text-sm md:text-lg">Need Help Right Now?</h3>
              </div>
              
              <p className="text-gray-600 mb-2 md:mb-4 text-xs md:text-base">
                Immediate website fix service <span className="font-medium">(3 support credits)</span>
              </p>
              
              <div className="flex items-center mb-2 md:mb-4 bg-amber-50 rounded-lg p-1.5 md:p-2 justify-center">
                <span className="text-xl md:text-3xl font-bold text-gray-800">$89</span>
                <span className="text-gray-600 ml-2 text-xs md:text-base">one-time</span>
              </div>
              
              <a 
                href="/dashboard/settings?tab=products" 
                className="block w-full bg-amber-500 text-white px-3 py-2 md:px-4 md:py-3 rounded-md font-medium text-center hover:bg-amber-600 transition-all shadow-md hover:shadow-lg text-xs md:text-base hover:scale-105"
              >
                Emergency Fix
              </a>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default Hero;